import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { Banner } from '../../shared/models/models';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-banners',
  standalone: false,
  template: `
    <div class="page-wrapper">
      <header class="top-bar">
        <div class="top-bar-content">
          <button mat-icon-button (click)="back()"><mat-icon>arrow_back</mat-icon></button>
          <div>
            <span class="page-title">{{ clientName }}</span>
            <span class="page-sub">Diseños</span>
          </div>
        </div>
      </header>

      <main class="page-content">
        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Buscar diseño</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input matInput [value]="search()" (input)="search.set($any($event.target).value)" placeholder="Nombre del diseño..." />
        </mat-form-field>

        @if (loading()) {
          <div class="banner-list">
            @for (i of skeletons; track i) {
              <div class="banner-row skeleton"></div>
            }
          </div>
        } @else if (filtered().length === 0) {
          <div class="empty-state">
            <mat-icon>image_not_supported</mat-icon>
            <p>No se encontraron diseños.</p>
          </div>
        } @else {
          <div class="banner-list">
            @for (banner of filtered(); track banner.id) {
              <div class="banner-row" (click)="goToAudit(banner)" role="button" tabindex="0">
                <div class="banner-thumb">
                  @if (banner.thumbnail_url) {
                    <img [src]="banner.thumbnail_url" [alt]="banner.name" />
                  } @else {
                    <mat-icon>image</mat-icon>
                  }
                </div>
                <div class="banner-info">
                  <span class="banner-name">{{ banner.name }}</span>
                  <span class="banner-meta">{{ relativeDate(banner.last_update_date) }}</span>
                </div>
                <span class="status-chip" [class]="'status-' + banner.status.toLowerCase()">
                  {{ statusLabel(banner.status) }}
                </span>
                @if (auditedIds().has(banner.id)) {
                  <span class="audit-chip" matTooltip="Tiene historial de auditoría">
                    <mat-icon>history</mat-icon>
                  </span>
                } @else {
                  <span class="audit-chip audit-chip--none" matTooltip="Sin historial de auditoría">
                    <mat-icon>history_toggle_off</mat-icon>
                  </span>
                }
                <mat-icon class="arrow">chevron_right</mat-icon>
              </div>
            }
          </div>
        }
      </main>
    </div>
  `,
  styleUrls: ['./banners.component.scss']
})
export class BannersComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  banners = signal<Banner[]>([]);
  auditedIds = signal<Set<number>>(new Set());
  loading = signal(true);
  search = signal('');
  skeletons = Array(8);
  clientName = '';
  clientId = 0;

  private readonly statusOrder: Record<string, number> = { ACTIVE: 0, SCHEDULED: 1, DISABLED: 2, DELETED: 3 };

  filtered = computed(() => {
    const q = this.search().toLowerCase();
    return this.banners()
      .filter(b => b.name.toLowerCase().includes(q))
      .sort((a, b) => (this.statusOrder[a.status] ?? 9) - (this.statusOrder[b.status] ?? 9));
  });

  ngOnInit(): void {
    this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));
    this.clientName = history.state?.clientName ?? `Cliente ${this.clientId}`;
    this.api.getBanners(this.clientId).pipe(
      switchMap(banners => {
        this.banners.set(banners);
        this.loading.set(false);
        const ids = banners.map(b => b.id);
        return ids.length ? this.api.getAuditStatus(ids) : [];
      })
    ).subscribe({
      next: ids => this.auditedIds.set(new Set(ids)),
      error: () => {},
    });
  }

  relativeDate(dateStr?: string): string {
    if (!dateStr) return '';
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
    if (days === 0) return 'Modificado hoy';
    if (days === 1) return 'Modificado hace 1 día';
    return `Modificado hace ${days} días`;
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      ACTIVE: 'Activo', DISABLED: 'Inactivo', SCHEDULED: 'Programado', DELETED: 'Eliminado',
    };
    return map[status] ?? status;
  }

  goToAudit(banner: Banner): void {
    this.router.navigate(['/banners', banner.id, 'audit'], {
      state: { bannerName: banner.name, clientName: this.clientName, clientId: this.clientId },
    });
  }

  back(): void { this.router.navigate(['/clients']); }
}
